import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ArrowUpDown, Loader2 } from 'lucide-react';
import { API, Storage } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import moment from 'moment';
import Fuse from 'fuse.js';
import { debounce } from 'lodash';
import { PageHeader } from '../../components/page-header';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';

const gql: any = gqlTag;

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

interface Reward {
  id: string;
  name: string;
  imageName: string;
  imageType: string;
  validFrom: string;
  validTo: string;
  description: string;
  rewardType: string;
  value: number;
  valueType: string;
  cost: number;
  costType: string;
  remainingCount: number;
  personLimit: number;
  limitCount: number;
  isDeleted: boolean;
}

function RewardListItem({
  reward,
  onClick,
}: {
  reward: Reward;
  onClick: () => void;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    async function loadImage() {
      if (reward.imageName && reward.imageName !== '') {
        if (
          reward.imageType === 'gif_url' ||
          reward.imageType === 'giftcard_url' ||
          reward.imageType === 'trophy'
        ) {
          setImageUrl(reward.imageName);
        } else {
          const url = await Storage.get(reward.imageName);
          setImageUrl(url as string);
        }
      }
    }
    loadImage();
  }, [reward.imageName, reward.imageType]);

  return (
    <div
      onClick={onClick}
      className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="size-11 rounded-full bg-muted border border-border flex items-center justify-center overflow-hidden shrink-0">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={reward.name}
            className="w-full h-full object-contain"
          />
        ) : (
          <span className="text-[10px] text-muted-foreground">No Img</span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {reward.name}
        </p>
        <p className="text-sm text-muted-foreground">
          {moment(reward.validFrom).format('MM/DD/YY')}
        </p>
      </div>
    </div>
  );
}

export function RewardsArchivePage() {
  const navigate = useNavigate();

  const [rewards, setRewards] = useState<Reward[]>([]);
  const [filteredRewards, setFilteredRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

  const fuseOptions = {
    keys: ['name'],
    includeScore: true,
    threshold: 0.6,
  };

  const fetchArchivedRewards = useCallback(async () => {
    try {
      setLoading(true);

      const archivedRewardsQuery = gql`
        query ListArchivedRewards(
          $orgID: ID!
          $today: String!
          $sortDirection: ModelSortDirection
          $nextToken: String
        ) {
          listRewardsByOrgName(
            orgID: $orgID
            filter: { isDeleted: { eq: false }, validTo: { lt: $today } }
            sortDirection: $sortDirection
            nextToken: $nextToken
          ) {
            items {
              id
              name
              imageName
              imageType
              validFrom
              validTo
              isDeleted
              createdBy
              orgID
              description
              rewardType
              value
              valueType
              cost
              costType
              remainingCount
              personLimit
              limitCount
            }
            nextToken
          }
        }
      `;

      const allRewards: Reward[] = [];
      let nextToken: string | null = null;

      do {
        const listArchivedRewardsRes: any = await API.graphql({
          query: archivedRewardsQuery as any,
          variables: {
            orgID: global.me.orgID,
            today: new Date().toISOString().split('T')[0],
            sortDirection,
            nextToken,
          },
        });

        const items = listArchivedRewardsRes.data.listRewardsByOrgName.items;

        for (const reward of items) {
          if (!reward.isDeleted) {
            allRewards.push(reward);
          }
        }

        nextToken = listArchivedRewardsRes.data.listRewardsByOrgName.nextToken;
      } while (nextToken !== null);

      setRewards(allRewards);
      setFilteredRewards(allRewards);
    } catch (error) {
      console.error('Error fetching archived rewards:', error);
    } finally {
      setLoading(false);
    }
  }, [sortDirection]);

  useEffect(() => {
    fetchArchivedRewards();
  }, [fetchArchivedRewards]);

  const handleSearch = useCallback(
    debounce((text: string) => {
      setSearchText(text);

      if (!text.trim()) {
        setFilteredRewards(rewards);
        return;
      }

      const fuse = new Fuse(rewards, fuseOptions);
      const searchResults = fuse.search(text);
      const formattedResults = searchResults.map((result) => result.item);
      setFilteredRewards(formattedResults);
    }, 300),
    [rewards]
  );

  const handleRewardClick = (reward: Reward) => {
    navigate('/v2/performance/rewards/details', {
      state: {
        ...reward,
        returnTo: '/v2/performance/rewards/archive',
      },
    });
  };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <PageHeader
        title="Rewards Archive"
        avatar={{
          src: global.me?.imageName || undefined,
          name: global.me?.name,
        }}
      />

      <div className="flex-1 overflow-auto px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm mb-6">
          <button
            onClick={() => navigate('/v2/performance')}
            className="text-primary hover:underline"
          >
            Performance
          </button>
          <span className="text-muted-foreground">/</span>
          <button
            onClick={() => navigate('/v2/performance/rewards')}
            className="text-primary hover:underline"
          >
            Rewards
          </button>
          <span className="text-muted-foreground">/</span>
          <span className="text-muted-foreground">Archive</span>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search"
            className="pl-10"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-base font-medium text-foreground">
            All ({searchText ? filteredRewards.length : rewards.length})
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <ArrowUpDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setSortDirection('ASC')}
                className={cn(sortDirection === 'ASC' && 'bg-accent')}
              >
                From A-Z
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setSortDirection('DESC')}
                className={cn(sortDirection === 'DESC' && 'bg-accent')}
              >
                From Z-A
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && filteredRewards.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <p className="text-muted-foreground">
              {searchText ? 'No rewards found' : 'No archived rewards'}
            </p>
          </div>
        )}

        {!loading && filteredRewards.length > 0 && (
          <div className="space-y-2">
            {filteredRewards.map((reward) => (
              <RewardListItem
                key={reward.id}
                reward={reward}
                onClick={() => handleRewardClick(reward)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
